import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProductsPaginated as fetchProductsService,
  fetchCategories as fetchCategoriesService,
  addProduct,
  updateProduct,
  deleteProduct,
  fetchProductById,
  fetchProductsByCategory,
  fetchProductReviews,
  addProductReview,
  getWishlist,
  addToWishlist,
  removeFromWishlist,
  checkIfWishlisted,
  importProduct,
} from "@/services/productService";

/* =========================================================
   🔹 Async Thunks
========================================================= */

// ✅ Fetch ALL products (admin / fallback only)
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const products = await fetchProductsService();
      return Array.isArray(products) ? products : [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch products");
    }
  }
);

// ✅ Fetch paginated products (Home page)
export const fetchProductsPaginated = createAsyncThunk(
  "products/fetchProductsPaginated",
  async ({ page = 1, limit = 20, category, maxPrice }, { rejectWithValue }) => {
    try {
      const res = await fetchProductsService({
        page,
        limit,
        category,
        maxPrice,
      });

      // ✅ FIX: convert grouped data → flat array
      const flatProducts = res.groups
        ? Object.values(res.groups).flat()
        : [];

      return {
        data: flatProducts,
        page,
        limit,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch paginated products");
    }
  }
);

// ✅ Single product
export const fetchProductByIdThunk = createAsyncThunk(
  "products/fetchProductById",
  async (id, { rejectWithValue }) => {
    try {
      const product = await fetchProductById(id);
      if (!product) throw new Error("Product not found");
      return product;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Categories
export const fetchCategories = createAsyncThunk(
  "products/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCategoriesService();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Reviews
export const fetchProductReviewsThunk = createAsyncThunk(
  "products/fetchProductReviews",
  async (productId, { rejectWithValue }) => {
    try {
      return await fetchProductReviews(productId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProductsByCategoryThunk = createAsyncThunk(
  "products/fetchProductsByCategory",
  async (category, { rejectWithValue }) => {
    try {
      return await fetchProductsByCategory(category);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addProductReviewThunk = createAsyncThunk(
  "products/addProductReview",
  async ({ productId, content, rating }, { dispatch, rejectWithValue }) => {
    try {
      const res = await addProductReview(productId, content, rating);
      dispatch(fetchProductReviewsThunk(productId));
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Wishlist
export const fetchWishlistThunk = createAsyncThunk(
  "products/fetchWishlist",
  async (_, { rejectWithValue }) => {
    try {
      return await getWishlist();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addToWishlistThunk = createAsyncThunk(
  "products/addToWishlist",
  async (productId, { dispatch }) => {
    await addToWishlist(productId);
    dispatch(fetchWishlistThunk());
  }
);

export const removeFromWishlistThunk = createAsyncThunk(
  "products/removeFromWishlist",
  async (productId, { dispatch }) => {
    await removeFromWishlist(productId);
    dispatch(fetchWishlistThunk());
  }
);

export const checkIfWishlistedThunk = createAsyncThunk(
  "products/checkIfWishlisted",
  async (productId) => {
    return await checkIfWishlisted(productId);
  }
);

// ✅ Admin mutations
export const createProduct = createAsyncThunk(
  "products/createProduct",
  async (productData, { dispatch }) => {
    const res = await addProduct(productData);
    dispatch(fetchProducts());
    return res;
  }
);

export const editProduct = createAsyncThunk(
  "products/editProduct",
  async ({ id, productData }, { dispatch }) => {
    const res = await updateProduct(id, productData);
    dispatch(fetchProducts());
    return res;
  }
);

export const removeProduct = createAsyncThunk(
  "products/removeProduct",
  async (id, { dispatch }) => {
    await deleteProduct(id);
    dispatch(fetchProducts());
  }
);

export const importProductThunk = createAsyncThunk(
  "products/importProduct",
  async (productData, { dispatch }) => {
    const res = await importProduct(productData);
    dispatch(fetchProducts());
    return res;
  }
);

/* =========================================================
   🧩 Slice
========================================================= */

const productSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    categories: [],
    reviews: [],
    wishlist: [],
    selectedProduct: null,

    loading: false,
    pageLoading: false, // 👈 important for infinite scroll
    error: null,

    hasMore: true,
  },
  reducers: {
    clearProductError: (state) => {
      state.error = null;
    },
    resetProducts: (state) => {
      state.products = [];
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ===== Paginated Products (FAST) ===== */
      .addCase(fetchProductsPaginated.pending, (state, action) => {
        state.pageLoading = action.meta.arg.page > 1;
        state.loading = action.meta.arg.page === 1;
      })
      .addCase(fetchProductsPaginated.fulfilled, (state, action) => {
        const { data, page, limit } = action.payload;

        if (page === 1) {
          state.products = data;
        } else {
          state.products.push(...data);
        }

        state.hasMore = data.length === limit;
        state.loading = false;
        state.pageLoading = false;
      })
      .addCase(fetchProductsPaginated.rejected, (state, action) => {
        state.loading = false;
        state.pageLoading = false;
        state.error = action.payload;
      })

      /* ===== Full product list ===== */
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload;
        state.loading = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== Categories ===== */
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload || [];
      })

      /* ===== Product Details ===== */
      .addCase(fetchProductByIdThunk.fulfilled, (state, action) => {
        state.selectedProduct = action.payload;
      })

      /* ===== Reviews ===== */
      .addCase(fetchProductReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload?.reviews || [];
      })

      /* ===== Wishlist ===== */
      .addCase(fetchWishlistThunk.fulfilled, (state, action) => {
        state.wishlist = action.payload || [];
      })

      /* ===== Global error catcher ===== */
      .addMatcher(
        (action) => action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.pageLoading = false;
          state.error = action.payload || action.error?.message;
        }
      );
  },
});

export const { clearProductError, resetProducts } = productSlice.actions;
export default productSlice.reducer;

